import { OrderSupportModalHeader } from '../OrderSupportModalHeader'
import React, { useEffect, useRef, useState } from 'react'
import { Button, Heading, Link, Modal, Text } from '@afterpaytouch/core'
import { ErrorBoundary } from '../../integrations/sentry'
import { useTranslation } from 'next-i18next'
import { TrackOnMount, useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { useDisputeCreationModalContext } from './DisputeCreationModalContext'
import { useDisputeTranslation } from './hooks'

export const contactSupportProperties = { context: 'contact_afterpay_support', platform: 'web', destination: 'support_request_form' }

export const useContactSupportTracking = () => {
  const { logEvent } = useAmplitudeWithEnduringEventProperties(contactSupportProperties)
  return (event: Parameters<typeof logEvent>[0], properties: Record<string, unknown> = {}) => {
    try {
      logEvent(event, { ...contactSupportProperties, ...properties })
    } catch {
      // Analytics delivery must not block support access.
    }
  }
}

export const validSupportUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

interface ContactAfterpaySupportProps {
  reopenGetHelpModal: () => void
}

const SupportContent = ({ renderFailed = false, reopenGetHelpModal }: ContactAfterpaySupportProps & { renderFailed?: boolean }) => {
  const t = useDisputeTranslation()
  const { t: common } = useTranslation('common')
  const { order, closeModal } = useDisputeCreationModalContext()
  const track = useContactSupportTracking()
  const [failed, setFailed] = useState(false)
  const attempt = useRef(0)
  const reportedContext = useRef(false)
  const url = common('common:urls.helpUrl.newRequest')
  const merchant = renderFailed ? undefined : order?.merchant?.merchantDisplayName || order?.merchant?.name
  const reference = !renderFailed && order?.id ? String(order.id) : undefined
  const missingContext = !merchant || !reference

  useEffect(() => {
    if (!reportedContext.current && (renderFailed || missingContext)) {
      reportedContext.current = true
      track(TrackingEvent.FAILED_CREATE_DISPUTE_CONTACT_AFTERPAY_SUPPORT, {
        failure_stage: renderFailed ? 'render' : 'load_context',
        failure_code: renderFailed ? 'render_error' : 'missing_order_context',
        recoverable: true,
        attempt_number: 0,
      })
    }
  }, [missingContext, renderFailed, track])

  const fail = (code: string) => {
    setFailed(true)
    track(TrackingEvent.FAILED_CREATE_DISPUTE_CONTACT_AFTERPAY_SUPPORT, {
      failure_stage: 'open_external',
      failure_code: code,
      recoverable: true,
      attempt_number: attempt.current,
    })
  }

  const backToHelpModal = () => {
    closeModal()
    reopenGetHelpModal()
  }

  const openSupport = () => {
    attempt.current += 1
    track(failed ? TrackingEvent.PRESSED_CREATE_DISPUTE_CONTACT_AFTERPAY_SUPPORT_RETRY : TrackingEvent.PRESSED_CREATE_DISPUTE_CONTACT_AFTERPAY_SUPPORT)
    if (!validSupportUrl(url)) {
      fail('invalid_support_url')
      return
    }
    try {
      const tab = window.open('about:blank', '_blank')
      if (!tab) {
        fail('popup_blocked')
        return
      }
      tab.opener = null
      tab.location.href = url
      setFailed(false)
    } catch {
      fail('launcher_exception')
    }
  }

  const openInSameTab = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    attempt.current += 1
    track(TrackingEvent.PRESSED_CREATE_DISPUTE_CONTACT_AFTERPAY_SUPPORT_RETRY)
    try {
      window.location.assign(url)
    } catch {
      fail('launcher_exception')
    }
  }

  return (
    <>
      {!renderFailed && (
        <TrackOnMount bestEffort eventName={TrackingEvent.VIEWED_CREATE_DISPUTE_CONTACT_AFTERPAY_SUPPORT} eventProps={contactSupportProperties} />
      )}
      <OrderSupportModalHeader onBack={backToHelpModal} onClose={closeModal}>
        <Heading size='L'>{t('contactAfterpaySupport.head')}</Heading>
      </OrderSupportModalHeader>
      <Modal.Content>
        <Text renderAs='p'>{t('contactAfterpaySupport.body')}</Text>
        <Text renderAs='p'>{t('contactAfterpaySupport.clarification')}</Text>
        {merchant && <Text renderAs='p'>{merchant}</Text>}
        {reference && <Text renderAs='p'>{t('contactAfterpaySupport.orderReference', { reference })}</Text>}
        {(renderFailed || missingContext) && <p role='alert'>{t('contactAfterpaySupport.contextError')}</p>}
        {failed && <p role='alert'>{t('contactAfterpaySupport.error')}</p>}
        <div className='mt-4'>
          <Button padding='Fluid' size='Large' onClick={openSupport}>
            {t(failed ? 'contactAfterpaySupport.retry' : 'contactAfterpaySupport.primary')}
          </Button>
        </div>
        {failed && validSupportUrl(url) && (
          <div className='mt-4'>
            <Link kind='Underline' href={url} onClick={openInSameTab}>
              {t('contactAfterpaySupport.sameTab')}
            </Link>
          </div>
        )}
        <div className='mt-4'>
          <Button.Ghost padding='Fluid' size='Large' onClick={closeModal}>
            {t('contactAfterpaySupport.back')}
          </Button.Ghost>
        </div>
      </Modal.Content>
    </>
  )
}

export const ContactAfterpaySupport = ({ reopenGetHelpModal }: ContactAfterpaySupportProps) => (
  <ErrorBoundary enabled fallbackError={<SupportContent renderFailed reopenGetHelpModal={reopenGetHelpModal} />}>
    <SupportContent reopenGetHelpModal={reopenGetHelpModal} />
  </ErrorBoundary>
)
